import React, { createContext, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import GradientButton from '../components/GradientButton';

// Types
export type FeedbackType = 'success' | 'error' | 'warning' | 'info';

interface ModalConfig {
  title: string;
  description?: string;
  type?: FeedbackType;
  actionLabel?: string;
  onAction?: () => void;
}

// Context
interface ModalContextType {
  showModal: (config: ModalConfig) => void;
  hideModal: () => void;
}

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Provider Component
export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ModalConfig>({
    title: '',
    type: 'info',
    actionLabel: 'OK'
  });

  const showModal = useCallback((modalConfig: ModalConfig) => {
    setConfig(modalConfig);
    setIsOpen(true);
  }, []);

  const hideModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Modal Component
  const Modal = () => {
    const handleAction = () => {
      if (config.onAction) {
        config.onAction();
      }
      hideModal();
    };

    const icons = {
      success: <CheckCircle2 className="w-6 h-6 text-green-500" />,
      error: <XCircle className="w-6 h-6 text-red-500" />,
      warning: <AlertCircle className="w-6 h-6 text-yellow-500" />,
      info: <AlertCircle className="w-6 h-6 text-blue-500" />
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div 
          className="fixed inset-0 bg-black/50" 
          onClick={hideModal}
        />
        <div className="relative z-50 w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              {icons[config.type || 'info']}
              {config.title}
            </h2>
            {config.description && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {config.description}
              </p>
            )}
          </div>
          <div className="mt-6 flex justify-center">
            <GradientButton
              onClick={handleAction}
              className={`w-full sm:w-auto ${
                config.type === 'error' ? '!from-red-500 !to-red-600' : ''
              }`}
            >
              {config.actionLabel || 'Ok'}
            </GradientButton>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      {children}
      <Modal />
    </ModalContext.Provider>
  );
}
